// Vérification de licence au démarrage + acceptation des CGU (voir pages/licenseGate.ts).
import { getPraetoriaApiToken, PRAETORIA_API_BASE_URL } from "../config";
import { buildApiError } from "./errors";

const LICENSE_CHECK_TIMEOUT_MS = 15000;

export type LicenseCheckResult =
  | "valid"
  | "invalid_license"
  | "identity_error"
  | "cgu_required"
  | "service_unavailable"
  | "technical_error";

export async function checkLicense(graphToken: string): Promise<LicenseCheckResult> {
  const token = getPraetoriaApiToken();
  if (!token) {
    return "identity_error";
  }

  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), LICENSE_CHECK_TIMEOUT_MS);

  try {
    const response = await fetch(`${PRAETORIA_API_BASE_URL}/app/email/license/check`, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ graph_access_token: graphToken }),
      signal: controller.signal,
    });
    if (response.ok) {
      return "valid";
    }
    if (response.status === 403) {
      return "invalid_license";
    }
    if (response.status === 401) {
      return "identity_error";
    }
    if (response.status === 451) {
      return "cgu_required";
    }
    if (response.status === 503) {
      return "service_unavailable";
    }
    return "technical_error";
  } catch {
    return "technical_error";
  } finally {
    clearTimeout(timeoutId);
  }
}

// Enregistre l'acceptation des CGU par le notaire (case cochée + clic "j'accepte" sur
// l'écran #cgu-required, voir pages/licenseGate.ts). `version` doit être CGU_VERSION (config.ts)
// -- si le serveur répond 409, c'est que ce build du plugin est en décalage avec
// PRAETOR_CGU_VERSION côté serveur (voir le commentaire sur CGU_VERSION dans config.ts).
export async function acceptCgu(graphToken: string, version: string): Promise<void> {
  const token = getPraetoriaApiToken();
  if (!token) {
    throw new Error("Token API Praetoria manquant (PRAETORIA_API_TOKEN).");
  }

  const response = await fetch(`${PRAETORIA_API_BASE_URL}/app/email/cgu/accept`, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ graph_access_token: graphToken, version }),
  });

  if (!response.ok) {
    throw await buildApiError(response);
  }
}
